// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll from "@conklin/App/Components/Common/FancyScroll";
import { xHeaderUpdate } from "@conklin/App/Handlers/XNG/Header";
import { getSettingsCollection } from "@conklin/App/Store/Selectors/SettingsSelectors";
import LazyImage, { LazyImageProps } from "@conklin/MobiusOverrides/LazyImage";
import { constants } from "@conklin/XDL/constants";
import { color, ellipsis, styledScrollbar, xClassNames } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps, ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import isMobile from "ismobilejs";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";
import FloutingUiMenu, { CloseMenuTreeOnClick } from "@conklin/App/Components/Menu/DropdownMenu";
import { OffsetOptions } from "@floating-ui/react";

export type MenuVariants = "primary" | "secondary";

const DEFAULT_BOUNDARY_SELECTOR = "#main-navigation-container";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    // XNG-Changes
    content?: any;
    showBrandLink?: boolean;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
        // XNG-Changes
        contentWrapper?: InjectableCss;
        contentTitle?: TypographyPresentationProps;
        contentText?: TypographyPresentationProps;
        contentLink?: LinkPresentationProps;
        contentImage?: LazyImageProps;
        brandLinkWrapper?: InjectableCss;
        brandLink?: LinkPresentationProps;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
    // XNG-Changes
    content?: any;
    displayBrandLink?: boolean;
    /**
     * XNG-Changes
     * Choose 'Primary' when the subMenu needs to match the parent menu height.
     */
    menuVariant?: MenuVariants;
    referenceIndex?: number;
    offset?: OffsetOptions;
    menuBoundary?: string;
    /**
     * menu item index
     */
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{ id: string; className: string }>;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /** XNG-Changes: tippy trigger prop */
        tippyTrigger?: string;
        /** XNG-Changes: To prevent Menu close by click on trigger */
        hideOnClick?: boolean;
    }
>;

// XNG-Changes
const mapStateToProps = (state: ApplicationState) => ({
    brandsPageUrl: getPageLinkByPageType(state, "BrandsPage")?.url,
    // XNG-Changes
    lockCustomerSelection: getSettingsCollection(state).customSettings?.lockCustomerSelection,
});
const mapDispatchToProps = {
    xHeaderUpdate,
};

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps &
    MenuPresentationProps &
    ThemeProps<BaseTheme> &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

const MenuStyle = styled.div<MenuStyleProps>`
    display: flex;
    flex-direction: column;
    background: ${color("common.white")};
    width: ${getProp("width", 175)}px;
    max-width: 25vw;
    height: 100%;
    max-height: calc(calc(100vh - ${constants.header.size.height}px) * 0.8);
    padding: 6px 0;
    list-style: none;
    margin: 0;
    overflow: auto;
    border: 1px solid ${color("common.ui6")};
    border-top: none;
    row-gap: 2px;
    min-height: 425px;
    ${styledScrollbar(8)}
    ${injectCss}
    // XNG-Changes
    ${({ displayBrandLink }: any) => {
        return !displayBrandLink
            ? css`
                  min-height: 200px;
              `
            : null;
    }}
`;

type MenuWrapperProps = { canBeOpen?: boolean; isOpen?: true } & InjectableCss;
// XNG-Change
const MenuWrapper = styled.nav<MenuWrapperProps>`
    &:hover,
    &[data-open] {
        .trigger-item {
            position: relative;
            ${TypographyStyle} {
                color: ${color("text.ui10")};
            }
            ${IconWrapper} {
                color: ${color("common.ui10")};
                transform: rotate(180deg);
            }
        }
    }

    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean; weight?: number }>`
    color: ${getColor("text")};
    display: inline;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: ${({ weight }) => weight};
    letter-spacing: 0;
    line-height: 17px;
    ${ellipsis(2)}
`;

const menuItemColorStyle = {
    default: {
        background: "transparent",
        text: "text.main",
        icon: "common.ui4",
    },
    active: {
        background: "common.ui11",
        text: "common.ui1",
        icon: "common.ui1",
    },
};

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    & > a {
        padding: 6px 6px 6px 12px;
    }

    & + [data-tippy-root] {
        &,
        & > div {
            height: 100%;
            max-height: 100%;
        }
    }

    &:focus {
        outline: none;
    }

    ${StyledButton} {
        display: inline-block;
        width: calc(100% - 15px);
    }

    ${IconWrapper} {
        color: ${color(menuItemColorStyle.default.icon)};
        width: 24px;
        height: 14px;
        top: 50%;
        transform: translateY(-50%);
        padding-right: 8px;
    }

    &:hover,
    &[data-open] {
        background: ${color(menuItemColorStyle.active.background)};
        ${IconWrapper} {
            color: ${color(menuItemColorStyle.active.icon)};
        }
        ${MenuItemText} {
            color: ${color(menuItemColorStyle.active.text)};
        }
    }

    ${injectCss}
`;

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
    isChildShow: boolean;
}

export const DEFAULT_MENU_WIDTH = 262;

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: DEFAULT_MENU_WIDTH,
        tippyTrigger: "mouseenter focusin",
        hideOnClick: false,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
        isChildShow: false,
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    // XNG-Changes: add content
                    const {
                        menuItems,
                        menuTrigger,
                        isOpen,
                        mergeCss,
                        content,
                        displayBrandLink = false,
                        menuVariant,
                        offset,
                        referenceIndex,
                        menuBoundary,
                        ...otherProps
                    } = this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;

                    const renderMenuItems = (
                        parentDepth: number,
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                    ) => {
                        // XNG-Changes
                        const parentMenu = React.createRef<HTMLDivElement | null>();
                        return (
                            <>
                                {displayBrandLink && currentDepth === 1 && (
                                    <StyledWrapper {...this.props.cssOverrides?.brandLinkWrapper}>
                                        <CloseMenuTreeOnClick>
                                            <Link href={this.props.brandsPageUrl} {...this.props.cssOverrides?.brandLink}>
                                                {translate("Shop by Brand")}
                                            </Link>
                                        </CloseMenuTreeOnClick>
                                    </StyledWrapper>
                                )}
                                {childMenuItems
                                    ?.filter(item => !item.excludeFromNavigation)
                                    .map((item, index) => {
                                        /**
                                         * XNG-Changes
                                         */
                                        const maxDepth = item.maxDepth || parentDepth;
                                        const hasChildren =
                                            item.children?.some(o => !o.excludeFromNavigation) &&
                                            currentDepth < maxDepth;
                                        // XNG-Changes: hide Change Customer when lockCustomerSelection is true
                                        if (
                                            (item.type === "ChangeCustomerPage" &&
                                                this.props.lockCustomerSelection) ||
                                            !this.state.shouldRender
                                        ) {
                                            return null;
                                        }

                                        const menuItem = (
                                            <MenuItemStyle
                                                width={otherProps.width}
                                                css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                                key={`${item.title}_${index}`}
                                                className={xClassNames.append([
                                                    "MenuItem",
                                                    hasChildren ? "has-children" : "",
                                                ])}
                                                data-test-selector="menuItem"
                                                data-depth={currentDepth}
                                                onClick={() => {
                                                    this.closeMenuWithoutChildren(hasChildren);
                                                }}
                                            >
                                                <CloseMenuTreeOnClick onClick={() => this.closeMenu()}>
                                                    <Clickable
                                                        href={item.url}
                                                        target={item.openInNewWindow ? "_blank" : ""}
                                                        {...applyProp("menuItemClickableProps")}
                                                    >
                                                        <MenuItemText
                                                            hasChildren={hasChildren}
                                                            {...applyProp("menuItemTypographyProps")}
                                                            weight="normal"
                                                        >
                                                            {item.title}
                                                        </MenuItemText>
                                                        {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                                    </Clickable>
                                                </CloseMenuTreeOnClick>
                                            </MenuItemStyle>
                                        );
                                        return (
                                            <React.Fragment key={`${item.title}_${index}`}>
                                                {!hasChildren && menuItem}
                                                {hasChildren && (
                                                    <FloutingUiMenu
                                                        key={`FloutingUiMenu_${item.title}_${index}`}
                                                        menuTrigger={menuItem}
                                                        offset={0}
                                                        placement="right-start"
                                                        shift={{
                                                            boundary:
                                                                typeof document !== "undefined"
                                                                    ? document.querySelector(
                                                                          DEFAULT_BOUNDARY_SELECTOR,
                                                                      ) || document.body
                                                                    : "clippingAncestors",
                                                            padding: 16,
                                                        }}
                                                        variant={menuVariant}
                                                    >
                                                        <FancyScroll
                                                            refElement={parentMenu}
                                                            contentPaddingRight={0}
                                                            showArrow={true}
                                                            extendedStyles={{
                                                                css: css`
                                                                    height: 100%;
                                                                    line-height: 0;
                                                                `,
                                                            }}
                                                        >
                                                            <MenuStyle
                                                                width={otherProps.width}
                                                                ref={parentMenu}
                                                                className="MenuStyle"
                                                                data-depth={currentDepth}
                                                                css={applyStyledProp(
                                                                    ["cssOverrides", "menu"],
                                                                    resolvedMergeCss,
                                                                )}
                                                                // XNG-Changes
                                                                {...{ displayBrandLink }}
                                                            >
                                                                {renderMenuItems(
                                                                    maxDepth,
                                                                    item!.children!,
                                                                    currentDepth + 1,
                                                                )}
                                                            </MenuStyle>
                                                        </FancyScroll>
                                                    </FloutingUiMenu>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}

                                {content && (!!content.title || !!content.text) && currentDepth > 1 && (
                                    <StyledWrapper {...this.props.cssOverrides?.contentWrapper}>
                                        {!!content.title && (
                                            <Typography {...this.props.cssOverrides?.contentTitle}>
                                                {content.title}
                                            </Typography>
                                        )}
                                        {!!content.text && (
                                            <Typography {...this.props.cssOverrides?.contentText}>
                                                {content.text}
                                            </Typography>
                                        )}
                                        {!!content.link && (
                                            <CloseMenuTreeOnClick>
                                                <Link href={content.link} {...this.props.cssOverrides?.contentLink}>
                                                    {translate("Learn More")}
                                                </Link>
                                            </CloseMenuTreeOnClick>
                                        )}
                                        {!!content.image && (
                                            <LazyImage src={content.image} {...this.props.cssOverrides?.contentImage} />
                                        )}
                                    </StyledWrapper>
                                )}
                            </>
                        );
                    };

                    const descriptionId = otherProps.descriptionId || uniqueId();
                    const parentMenu = React.createRef<HTMLDivElement | null>();
                    return (
                        <>
                            <FloutingUiMenu
                                menuTrigger={
                                    <MenuWrapper
                                        css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                        {...{ "aria-labelledby": descriptionId }}
                                    >
                                        {React.cloneElement(menuTrigger, {
                                            id: descriptionId,
                                            className: `trigger trigger-item`,
                                        })}
                                    </MenuWrapper>
                                }
                                overlay={true}
                                offset={16}
                                placement="bottom-start"
                                shift={{
                                    boundary:
                                        typeof document !== "undefined"
                                            ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                                            : "clippingAncestors",
                                    padding: 16,
                                }}
                                variant={menuVariant}
                            >
                                <StyledWrapper
                                    css={css`
                                        width: ${otherProps.width || DEFAULT_MENU_WIDTH}px;
                                    `}
                                >
                                    <FancyScroll
                                        refElement={parentMenu}
                                        contentPaddingRight={0}
                                        showArrow={true}
                                        extendedStyles={{
                                            css: css`
                                                height: 100%;
                                                line-height: 0;
                                            `,
                                        }}
                                    >
                                        <MenuStyle
                                            width={otherProps.width}
                                            ref={parentMenu}
                                            className="MenuStyle"
                                            data-depth={1}
                                            css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                            // XNG-Changes
                                            {...{ displayBrandLink }}
                                        >
                                            {renderMenuItems(otherProps.maxDepth || 0, menuItems, 1)}
                                        </MenuStyle>
                                    </FancyScroll>
                                </StyledWrapper>
                            </FloutingUiMenu>

                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default connect(mapStateToProps, mapDispatchToProps)(withTheme(Menu));
export { MenuItemText };
